import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Paper, AppBar, Toolbar, Button, IconButton, Avatar, Snackbar, Alert, Chip, Collapse, Tooltip } from '@mui/material';
import AvatarGroup from '@mui/material/AvatarGroup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LockIcon from '@mui/icons-material/Lock';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StarIcon from '@mui/icons-material/Star';
import CheckIcon from '@mui/icons-material/Check';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LaptopIcon from '@mui/icons-material/Laptop';
import CastForEducationIcon from '@mui/icons-material/CastForEducation';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { CATALOG, TRACKS } from './data/catalog';
import { SHOW_PAYMENT_FLOW, usePricing } from './data/pricing';
import { useFlowstateAuth, getInitials } from './context/AuthContext';
import { useThemeContext } from './context/ThemeContext';
import { BrandLogo } from './components/BrandLogo';
import { SocialProofToast } from './components/SocialProofToast';
import { LogoutConfirmDialog } from './components/LogoutConfirmDialog';

export const CategorySelection = ({ injectedTrackId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const trackId = injectedTrackId || params.trackId;
  const { user, logout, createOrder, verifyPayment } = useFlowstateAuth();
  const { mode, toggleTheme } = useThemeContext();
  const { getPrice } = usePricing();
  const isLight = mode === 'light';

  // State for UI Feedback
  const [loadingProductId, setLoadingProductId] = useState(null);
  const [alertState, setAlertState] = useState({ open: false, message: '', severity: 'success' });
  const [expandedTopics, setExpandedTopics] = useState({});
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const toggleExpand = (e, key) => {
    e.stopPropagation();
    setExpandedTopics(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (trackId) {
      localStorage.setItem('flowstate_active_track', trackId);
    }
    window.scrollTo(0, 0);

    // Load Razorpay Script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [trackId]);

  const handlePurchase = async (moduleId) => {
    if (!user) {
      navigate(`/interview-prep/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    try {
      setLoadingProductId(moduleId);
      // 1. Create Order via AuthContext Mutation
      const order = await createOrder(moduleId);

      // 2. Check if Razorpay is loaded
      if (!window.Razorpay) {
        throw new Error('Payment system is not loaded. Please refresh the page and try again.');
      }

      // 3. Open Razorpay Checkout
      const options = {
        key: order.key_id, // Use key from backend response
        amount: order.amount,
        currency: order.currency,
        name: 'FlowState',
        description: `Unlock ${moduleId}`,
        order_id: order.id,
        handler: async (response) => {
          try {
            // 3. Verify Payment via AuthContext Mutation
            await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              moduleId
            });
            // Success! The query invalidation in AuthContext will refresh the user's purchased modules
            setAlertState({ open: true, message: `Successfully unlocked ${moduleId}!`, severity: 'success' });
          } catch (verifyErr) {
            setAlertState({ open: true, message: verifyErr.message || 'Verification failed.', severity: 'error' });
          }
        },
        prefill: {
          name: user.name,
          email: user.email
        },
        theme: {
          color: '#2563eb'
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        setAlertState({ open: true, message: response.error.description || 'Payment failed.', severity: 'error' });
      });
      rzp.open();

    } catch (err) {
      console.error('Purchase Error:', err);
      setAlertState({ open: true, message: err.message || 'Something went wrong with the payment', severity: 'error' });
    } finally {
      setLoadingProductId(null);
    }
  };

  // Filter catalog based on track — memoized to avoid re-calculating on every render
  const track = trackId ? TRACKS[trackId] : null;
  const filteredCategoryKeys = useMemo(() => (
    track ? track.categories : Object.keys(CATALOG)
  ), [track, trackId]);

  const cardColors = {
    javascript: '#f59e0b', // amber
    react: '#3b82f6', // blue
    systemDesign: '#10b981', // green
    dsa: '#8b5cf6', // purple
    frontendSystemDesign: '#ec4899', // pink
    interviewPrep: '#14b8a6', // teal
    aiFundamentals: '#eab308', // yellow
    aiEngineering: '#ef4444', // red
    aiInterviewPrep: '#14b8a6', // teal
    mentorship: '#ec4899', // pink
    foundation: '#f97316', // vivid orange — Zero to Senior
    completeInterviewKit: '#06b6d4', // cyan — Complete Interview Kit
  };



  const isFree = !SHOW_PAYMENT_FLOW;
  const isTrackPurchased = trackId ? ((isFree && !!user) || user?.purchasedModules?.includes(trackId)) : true;

  // Fallback for invalid track
  if (trackId && !track) {
    navigate('/interview-prep');
    return null;
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      width: '100vw',
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: isLight
        ? 'radial-gradient(ellipse at 50% 0%, #cbd5e1 0%, #f8fafc 65%)'
        : 'radial-gradient(ellipse at 50% 0%, #1c1c2e 0%, #0a0a0c 65%)',
    }}>

      {/* ── Top Navigation ── */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'transparent', borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid #1f1f22' }}>
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 3, md: 6 }, minHeight: '64px !important' }}>
          <Box
            onClick={() => navigate('/interview-prep/')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <BrandLogo size="medium" isLight={isLight} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {trackId && (
              <Box
                onClick={() => {
                  const segs = location.pathname.split('/').filter(Boolean);
                  if (segs.length > 1) {
                    segs.pop();
                    navigate('/' + segs.join('/'));
                  } else {
                    navigate('/interview-prep');
                  }
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: isLight ? '#64748b' : '#a1a1aa', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, transition: 'color 0.15s', '&:hover': { color: isLight ? '#0f172a' : '#f1f5f9' }, mr: 1 }}
              >
                <ArrowBackIcon sx={{ fontSize: 15 }} />
                <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: '0.4px', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Back
                </Typography>
              </Box>
            )}

            <Tooltip title={isLight ? "Switch to Dark Mode" : "Switch to Light Mode"} arrow>
              <IconButton onClick={toggleTheme} sx={{ color: isLight ? '#0f172a' : '#f1f5f9' }}>
                {isLight ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip>

            {user ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Tooltip title={`Signed in as ${user.first_name || 'User'}`} arrow>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.85rem', fontWeight: 700 }}>
                    {getInitials(user)}
                  </Avatar>
                </Tooltip>
                <Tooltip title="Logout" arrow>
                  <IconButton onClick={() => setLogoutDialogOpen(true)} size="small" sx={{ color: isLight ? '#64748b' : '#a1a1aa' }}>
                    <LogoutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Button
                variant="outlined"
                onClick={() => navigate(`/interview-prep/login?redirect=${encodeURIComponent(window.location.pathname)}`)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                  color: isLight ? '#0f172a' : '#f1f5f9',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'transparent' }
                }}
              >
                Log In
              </Button>
            )}
          </Box>
        </Toolbar>
        <LogoutConfirmDialog 
          open={logoutDialogOpen} 
          onClose={() => setLogoutDialogOpen(false)} 
          onConfirm={() => {
            setLogoutDialogOpen(false);
            logout();
          }} 
        />
      </AppBar>

      {/* ── Main Content ── */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 3, md: 6 },
        py: 6,
      }}>
        <Box sx={{ maxWidth: { xs: '100%', lg: 1800, xl: 2400 }, width: '96%', mx: 'auto' }}>
          {/* Hero Section */}
          <Box sx={{
            mb: 4,
            textAlign: 'center',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '120%',
              height: '200%',
              background: 'radial-gradient(circle at center, rgba(59, 130, 246, 0.08) 0%, transparent 60%)',
              pointerEvents: 'none',
              zIndex: -1,
            }
          }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 3,
                px: 2.5,
                py: 0.75,
                borderRadius: '20px',
                border: isLight ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid rgba(59, 130, 246, 0.3)',
                background: isLight ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                animation: 'pulseGlow 3s infinite alternate',
                '@keyframes pulseGlow': {
                  '0%': { boxShadow: '0 0 0 0 rgba(59, 130, 246, 0.4)' },
                  '100%': { boxShadow: '0 0 20px 0 rgba(59, 130, 246, 0.1)' }
                }
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6', boxShadow: '0 0 10px #3b82f6' }} />
              <Typography variant="caption" sx={{ color: isLight ? '#2563eb' : '#60a5fa', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase' }}>
                Interactive Learning Engine V2.0
              </Typography>
            </Box>

            <Typography
              variant="h1"
              fontWeight="800"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                letterSpacing: '-1px',
                lineHeight: 1.2,
                mb: 2.5,
                background: isLight ? 'linear-gradient(to right, #1e293b 20%, #3b82f6 50%, #2563eb 80%)' : 'linear-gradient(to right, #f8fafc 20%, #93c5fd 50%, #60a5fa 80%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'shine 5s linear infinite',
                '@keyframes shine': {
                  'to': { backgroundPosition: '200% center' }
                }
              }}
            >
              {track ? track.title : "Master Engineering."}<br />
              {!track && "Don't Just Memorize It."}
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                color: isLight ? '#64748b' : '#94a3b8',
                fontWeight: 400,
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: { xs: '0.9rem', md: '1rem' },
                mb: 4
              }}
            >
              {track ? track.description : "Step into the FlowState. Launch interactive visualization engines, step through complex architectures frame-by-frame, and build indestructible mental models."}
            </Typography>

          </Box>

          {/* Cards Container */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: filteredCategoryKeys.length >= 4 ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
              xl: filteredCategoryKeys.length >= 5 ? 'repeat(5, 1fr)' : (filteredCategoryKeys.length >= 4 ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)')
            },
            justifyContent: 'center',
            gap: { xs: 3.5, xl: 4 }
          }}>
            {filteredCategoryKeys.map((key, index) => {
              const data = CATALOG[key];
              if (!data || data.marketing) return null; // Skip marketing cards in regular grid
              const isFree = !SHOW_PAYMENT_FLOW;
              const purchased = user?.purchasedModules || [];
              const priceData = getPrice(key);
              
              // Local fallback for pricing if backend returns 0/null
              const finalPrice = priceData.price || data.price || 0;
              const finalMrp = priceData.mrp || data.mrp || 0;

              let isOwned = (isFree && !!user) || purchased.includes(key);
              if (!isOwned) {
                for (const trackKey of Object.keys(TRACKS)) {
                  if (purchased.includes(trackKey) && TRACKS[trackKey].categories?.includes(key)) {
                    isOwned = true;
                    break;
                  }
                }
              }

              const primaryColor = cardColors[key] || '#3b82f6';

              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  style={{ display: 'flex' }}
                >
                  <Paper
                    elevation={0}
                    onClick={() => {
                      if (key === 'mentorship') {
                        navigate('/calendar-meeting');
                      } else {
                        // Build clean path by appending to current location
                        const currentPath = location.pathname.replace(/\/$/, "");
                        navigate(`${currentPath}/${key}`);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      p: { xs: 3, md: 4 },
                      borderRadius: 4,
                      background: isLight
                        ? 'rgba(255, 255, 255, 0.95)'
                        : 'rgba(23, 23, 26, 0.8)',
                      backdropFilter: 'blur(16px) saturate(180%)',
                      border: isLight
                        ? `2px solid ${primaryColor}25`
                        : `2px solid ${primaryColor}40`,
                      cursor: 'pointer',
                      alignItems: 'flex-start',
                      textAlign: 'left',
                      position: 'relative',
                      transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: isLight
                          ? `0 20px 40px -10px ${primaryColor}30`
                          : `0 20px 40px -10px ${primaryColor}40`,
                        border: `2px solid ${primaryColor}`,
                        '& .launch-btn-arrow': { transform: 'translateX(4px)' }
                      }
                    }}
                  >
                    {/* Top Bestseller Badge */}
                    {data.badge && (
                      <Box sx={{
                        position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                        background: 'linear-gradient(135deg, #db2777 0%, #ec4899 100%)',
                        color: '#fff', px: 2, py: 0.5, borderRadius: '20px', display: 'flex', alignItems: 'center', gap: 0.5,
                        boxShadow: '0 4px 10px rgba(219, 39, 119, 0.3)', zIndex: 5
                      }}>
                        <StarIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '1px' }}>{data.badge}</Typography>
                      </Box>
                    )}

                    {/* Subtitle & Icon Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, mt: data.badge ? 1 : 0 }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 40, height: 40, borderRadius: '12px',
                        background: isLight ? `${primaryColor}15` : `${primaryColor}20`,
                        color: primaryColor,
                        '& svg': { fontSize: '22px !important' }
                      }}>
                        <motion.div animate={{ y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}>
                          {data.icon}
                        </motion.div>
                      </Box>
                      <Box>
                        {data.subtitle && (
                          <Typography sx={{ color: primaryColor, fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {data.subtitle}
                          </Typography>
                        )}
                        <Typography variant="h6" fontWeight="900" sx={{ color: isLight ? '#0f172a' : '#f8fafc', fontSize: '1.28rem', letterSpacing: '-0.5px', lineHeight: 1.2 }}>
                          {data.title}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Description */}
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3, lineHeight: 1.6, fontSize: '0.92rem', fontWeight: 500 }}>
                      {data.description}
                    </Typography>

                    {/* Features List */}
                    {data.features && data.features.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3, width: '100%' }}>
                        {data.features.map((feature, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Box sx={{ mt: 0.2 }}>
                              {i === 1 ? <AutoAwesomeIcon sx={{ fontSize: 16, color: primaryColor }} /> :
                                (i === 2 ? <WorkspacePremiumIcon sx={{ fontSize: 16, color: primaryColor }} /> :
                                  <CheckIcon sx={{ fontSize: 16, color: primaryColor, fontWeight: 'bold' }} />)}
                            </Box>
                            <Typography sx={{ color: isLight ? '#475569' : '#cbd5e1', fontSize: '0.88rem', fontWeight: 600 }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Quick Peek Syllabus */}
                    {!isOwned && data.topics && data.topics.length > 0 && (
                      <Box sx={{ width: '100%', mb: 3, position: 'relative' }}>
                        <Box
                          onClick={(e) => toggleExpand(e, key)}
                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', py: 1.5, borderTop: isLight ? '1px dashed #cbd5e1' : '1px dashed #3f3f46' }}
                        >
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: isLight ? '#334155' : '#e2e8f0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quick Syllabus Peek</Typography>
                          {expandedTopics[key] ? <ExpandLessIcon sx={{ fontSize: 18, color: isLight ? '#94a3b8' : '#71717a' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: isLight ? '#94a3b8' : '#71717a' }} />}
                        </Box>
                        {expandedTopics[key] && (
                          <Box sx={{
                            position: 'absolute',
                            top: '100%',
                            left: -32, right: -32,
                            zIndex: 20,
                            bgcolor: isLight ? '#ffffff' : '#16161b',
                            border: isLight ? '1px solid #e2e8f0' : '1px solid #2d2d33',
                            borderRadius: 2,
                            p: 2,
                            boxShadow: isLight ? '0 8px 24px rgba(0,0,0,0.1)' : '0 8px 24px rgba(0,0,0,0.5)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                          }}>
                            {data.topics.slice(0, 5).map((topic, i) => (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: primaryColor }} />
                                <Typography sx={{ fontSize: '0.75rem', color: isLight ? '#475569' : '#94a3b8' }}>{topic.title}</Typography>
                              </Box>
                            ))}
                            {data.topics.length > 5 && <Typography sx={{ fontSize: '0.7rem', color: primaryColor, fontStyle: 'italic', mt: 0.5 }}>+ {data.topics.length - 5} more modules</Typography>}
                          </Box>
                        )}
                      </Box>
                    )}

                    <Box sx={{ flex: 1 }} /> {/* Spacer */}

                    {/* Urgency & Social Proof Row */}
                    {!isOwned && !isFree && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%', mb: 2, mt: 'auto' }}>

                        {/* Avatar Pile */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                          <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 26, height: 26, fontSize: 11, border: `2px solid ${isLight ? '#fff' : '#18181b'}`, bgcolor: isLight ? '#f1f5f9' : '#27272a', color: isLight ? '#94a3b8' : '#71717a' }, justifyContent: 'flex-end' }}>
                            <Avatar />
                            <Avatar />
                            <Avatar />
                            <Avatar />
                          </AvatarGroup>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: isLight ? '#64748b' : '#a1a1aa' }}>
                            +{((key || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 500) + 300} ENROLLED
                          </Typography>
                        </Box>

                        {/* Scarcity Pill */}
                        <Chip
                          icon={<LocalFireDepartmentIcon sx={{ color: '#ef4444 !important', fontSize: '14px !important' }} />}
                          label="High Demand"
                          size="small"
                          sx={{
                            height: 24, fontSize: '0.65rem', fontWeight: 800,
                            background: isLight ? '#fee2e2' : '#7f1d1d40',
                            color: isLight ? '#b91c1c' : '#fca5a5',
                            border: isLight ? '1px solid #f8717150' : '1px solid #ef444450'
                          }}
                        />
                      </Box>
                    )}

                    {/* Price & Action Button Area */}
                    <Box sx={{ width: '100%' }}>
                      {!isOwned && !isFree && finalPrice > 0 && (
                        <Typography sx={{ color: isLight ? '#0f172a' : '#f8fafc', fontWeight: 800, fontSize: '1.6rem', letterSpacing: '-0.5px', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          {finalMrp > 0 && (
                            <Typography component="span" sx={{ fontSize: '1rem', color: isLight ? '#94a3b8' : '#71717a', textDecoration: 'line-through', fontWeight: 600 }}>
                              ₹{finalMrp}
                            </Typography>
                          )}
                          ₹{finalPrice} <Box component="span" sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#10b981', background: '#10b98120', px: 1, py: 0.5, borderRadius: 1, ml: 1 }}>Best Value</Box>
                        </Typography>
                      )}

                      <Button
                        fullWidth
                        variant="contained"
                        disabled={loadingProductId === key}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (key === 'mentorship') {
                            navigate('/calendar-meeting');
                          } else if (isOwned || isFree || finalPrice === 0) {
                            const basePath = location.pathname.replace(/\/$/, "");
                            navigate(`${basePath}/${key}`);
                          } else {
                            handlePurchase(key);
                          }
                        }}
                        sx={{
                          py: 1.5,
                          borderRadius: '12px',
                          background: (isOwned || isFree || finalPrice === 0) ? `${primaryColor}15` : `linear-gradient(135deg, ${primaryColor}dd 0%, ${primaryColor} 100%)`,
                          color: (isOwned || isFree || finalPrice === 0) ? primaryColor : '#fff',
                          fontWeight: 800,
                          fontSize: '0.9rem',
                          textTransform: 'none',
                          boxShadow: (isOwned || isFree || finalPrice === 0) ? 'none' : `0 10px 25px -8px ${primaryColor}99`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: (isOwned || isFree || finalPrice === 0) ? `${primaryColor}25` : `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 100%)`,
                            boxShadow: (isOwned || isFree || finalPrice === 0) ? 'none' : `0 14px 30px -8px ${primaryColor}aa`,
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        {loadingProductId === key ? 'Processing...' : (
                          (isOwned || isFree || finalPrice === 0) ? (
                            <>Access Curriculum <ArrowForwardIcon className="launch-btn-arrow" sx={{ ml: 1, fontSize: 18 }} /></>
                          ) : (
                            <>Get Access &rarr;</>
                          )
                        )}
                      </Button>
                    </Box>
                  </Paper>
                </motion.div>
              );
            })}
          </Box>

          {/* ── High-Impact Marketing Cards for AI Track ── */}
          {trackId === 'ai' && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
                gap: 4
              }}>
                {['aiInterviewPrep', 'mentorship'].map((key, idx) => {
                  const data = CATALOG[key];
                  if (!data?.marketing) return null;
                  return (
                    <motion.div
                      key={key}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: idx * 0.2 }}
                    >
                      <Paper
                        elevation={0}
                        onClick={() => {
                          if (key === 'mentorship') {
                            navigate('/interview-prep/mentorship-inquiry');
                          } else {
                            const basePath = location.pathname.replace(/\/$/, "");
                            navigate(`${basePath}/${key}`);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          borderRadius: '32px',
                          overflow: 'hidden',
                          height: '100%',
                          background: isLight ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.03)',
                          backdropFilter: 'blur(20px)',
                          border: isLight ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(255, 255, 255, 0.08)',
                          boxShadow: isLight ? '0 25px 50px -12px rgba(0, 0, 0, 0.1)' : '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                          cursor: 'pointer',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderColor: 'rgba(139, 92, 246, 0.4)',
                            boxShadow: '0 30px 60px -15px rgba(0, 0, 0, 0.7), 0 0 30px rgba(139, 92, 246, 0.1)',
                          }
                        }}
                      >
                        {/* Illustration Side */}
                        <Box sx={{
                          flex: { sm: '0 0 240px', md: '0 0 280px' },
                          minHeight: { xs: '200px', sm: 'auto' },
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 4,
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(139, 92, 246, 0.15) 100%)',
                        }}>
                          <Box sx={{
                            position: 'absolute',
                            width: '200px',
                            height: '200px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.25) 0%, transparent 70%)',
                            filter: 'blur(40px)',
                            zIndex: 0
                          }} />
                          <Box
                            component="img"
                            src={data.marketing.image}
                            sx={{
                              width: '100%',
                              height: 'auto',
                              maxWidth: '220px',
                              zIndex: 1,
                              filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.3)) brightness(1.05)'
                            }}
                          />
                        </Box>

                        {/* Content Side */}
                        <Box sx={{
                          p: 4, // Guaranteed consistent padding
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          zIndex: 1,
                          justifyContent: 'flex-start'
                        }}>
                          <Typography variant="h4" sx={{
                            fontWeight: 900,
                            color: isLight ? '#0f172a' : '#f8fafc',
                            fontSize: { xs: '1.4rem', md: '1.55rem' },
                            mb: 1.5,
                            letterSpacing: '-0.8px',
                            minHeight: '4.5rem', // Fixed height to line up titles regardless of wrapping
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {data.title}
                          </Typography>
                          <Typography sx={{
                            color: isLight ? '#475569' : '#94a3b8',
                            fontSize: '0.98rem',
                            lineHeight: 1.65,
                            mb: 3,
                            fontWeight: 500,
                            letterSpacing: '0.1px',
                            minHeight: '3.5rem' // Standardize height of description area
                          }}>
                            {data.description}
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
                            {data.marketing.labels.map((label, lidx) => (
                              <Box key={lidx} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                {lidx === 0 ? <LaptopIcon sx={{ fontSize: 20, color: '#818cf8' }} /> :
                                  (key === 'mentorship' ? <CastForEducationIcon sx={{ fontSize: 20, color: '#818cf8' }} /> :
                                    <ArrowRightAltIcon sx={{ fontSize: 20, color: '#818cf8' }} />)}
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: isLight ? '#334155' : '#cbd5e1' }}>
                                  {label}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          <Box sx={{ mt: 'auto' }}>
                            <Button
                              variant="contained"
                              sx={{
                                px: 5,
                                py: 1.5,
                                borderRadius: '16px',
                                textTransform: 'none',
                                fontWeight: 800,
                                fontSize: '1rem',
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                boxShadow: '0 10px 20px -5px rgba(37, 99, 235, 0.4)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                  boxShadow: '0 15px 30px -5px rgba(37, 99, 235, 0.6)',
                                  transform: 'scale(1.02)'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              {data.marketing.cta}
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    </motion.div>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Global Dynamic Social Proof Render */}
      <SocialProofToast />

      {/* Footer */}
      <Box sx={{ textAlign: 'center', pb: 3, borderTop: isLight ? '1px solid #e2e8f0' : '1px solid #1f1f22', pt: 3, mt: 4 }}>
        <Typography variant="caption" sx={{ color: isLight ? '#64748b' : '#52525b', fontSize: '0.7rem' }}>
          FlowState · Interactive System Design, DSA & Web Architecture Learning
        </Typography>
      </Box>

      {/* Payment Feedback Snackbar */}
      <Snackbar
        open={alertState.open}
        autoHideDuration={6000}
        onClose={() => setAlertState(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlertState(prev => ({ ...prev, open: false }))}
          severity={alertState.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 700 }}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategorySelection;